<template>
  <ul class="flex min-w-0 flex-col gap-4" aria-label="Holiday schedule">
    <li v-for="holiday in holidays" :key="holiday[rowIdKey]" class="min-w-0">
      <article class="flex min-w-0 flex-col gap-4 rounded-xl border bg-card p-4 shadow-sm">
        <header class="flex min-w-0 flex-col items-start gap-2">
          <p v-if="showCompany" class="w-full break-words text-sm font-medium text-cyan-700 dark:text-cyan-300">
            {{ holiday.company?.name }}
          </p>
          <h2 class="w-full break-words text-lg font-semibold">
            {{ holiday.name }}
          </h2>
          <p class="flex items-start gap-2 text-sm text-muted-foreground">
            <CalendarDays aria-hidden="true" class="h-4 w-4 shrink-0" />
            <time :datetime="holiday.date">{{ formatDate(holiday.date) }}</time>
          </p>
          <Badge v-if="holiday.locked" variant="outline" class="gap-1 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
            <LockKeyhole aria-hidden="true" class="h-3.5 w-3.5" />
            Locked
          </Badge>
          <Badge v-else-if="holiday.confirmed" variant="outline" class="gap-1 border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
            <CircleCheck aria-hidden="true" class="h-3.5 w-3.5" />
            Confirmed
          </Badge>
          <Badge v-else variant="outline" class="border-amber-200 bg-amber-100 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
            Needs confirmation
          </Badge>
        </header>

        <dl class="flex min-w-0 flex-col gap-3 rounded-lg bg-muted/40 p-3 text-sm">
          <div class="flex flex-col gap-1">
            <dt class="text-muted-foreground">
              Schedule
            </dt>
            <dd class="font-medium">
              {{ scheduleSummary(holiday) }}
            </dd>
            <dd v-if="!holiday.confirmed && !holiday.notes" class="text-xs text-muted-foreground">
              Default schedule
            </dd>
          </div>
          <div v-if="holiday.notes" class="flex min-w-0 flex-col gap-1">
            <dt class="text-muted-foreground">
              Notes
            </dt>
            <dd class="whitespace-pre-wrap break-words">
              {{ holiday.notes }}
            </dd>
          </div>
        </dl>

        <div v-if="!holiday.locked || allowLockedEditing" class="flex flex-col gap-2">
          <Button v-if="!holiday.confirmed" type="button" class="min-h-12 w-full" :loading="savingIds.includes(holiday[rowIdKey])" :aria-label="`Confirm defaults for ${holiday.name}`" @click="emit('confirm-defaults', holiday)">
            <Check aria-hidden="true" class="h-4 w-4" />
            Confirm defaults
          </Button>
          <Button type="button" variant="outline" class="min-h-12 w-full" :disabled="savingIds.includes(holiday[rowIdKey])" :aria-label="`Edit schedule for ${holiday.name}`" @click="emit('edit', holiday)">
            <Pencil aria-hidden="true" class="h-4 w-4" />
            Edit schedule
          </Button>
        </div>
        <p v-else class="text-sm text-muted-foreground">
          Contact Centratel to change
        </p>
      </article>
    </li>
  </ul>
</template>

<script setup>
import { CalendarDays, Check, CircleCheck, LockKeyhole, Pencil } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatDate, scheduleSummary } from '../holidaySchedulePresentation.js'

defineProps({
  holidays: { type: Array, required: true },
  savingIds: { type: Array, default: () => [] },
  showCompany: { type: Boolean, default: false },
  allowLockedEditing: { type: Boolean, default: false },
  rowIdKey: { type: String, default: 'holiday_id' },
})

const emit = defineEmits(['confirm-defaults', 'edit'])
</script>
